// The Stock page's table as LineTable's columns and groups (truck stock, BL-203; was StockTable, BL-189
// consolidation), drawn from stock.levels' answer (stock-schemas.ts). One place: an item a row, by
// category (each group saying how many and how many are below min), with its code, what's on hand, its
// min and max, and when it was last counted; at our cost too, for whoever sees our cost (the answer
// carries costCents only for them). Every place: an item a row and a place a column, what's on hand in
// each. A level is said in words where it matters: "below min" or "below zero" beside the figure, in ink
// after StateMark's red bar (it needs someone; red is never small text here), "no min" (nothing refills
// it) and "to count" as Label's waiting word (DESIGN.md §2), "never" when it was never counted. An item's
// name opens its sheet; a place's name opens the place. While a tech's count is with the office (blind),
// his own van's figures say "hidden". Narrow (a phone), one place drops Last counted and At cost (the
// item's sheet has them) and says "to count" after the item's code; so does one place whose answer says
// nothing of when it was last counted (lastCounted absent), with no Last counted column.
import type { ReactNode } from 'react';
import type {
  LineCell,
  LineColumn,
  LineGroup,
  LineTableProps,
  LineWord,
} from '../components/LineTable/LineTable.tsx';
import { Link } from '../components/Link/Link.tsx';
import { hasNoMin, isBelowMin, qtyWords } from '../components/stock-words.ts';
import { money } from '../format.ts';
import type { StockItemRow, StockLevel, StockPlace } from '../stock-schemas.ts';

export interface StockLinesOptions {
  /** One place: its own columns. Several: a column each (Every place). */
  places: StockPlace[];
  rows: StockItemRow[];
  /** Opens an item's sheet (its levels, min and max); without it the names are words. */
  onOpenItem?: ((itemId: string) => void) | undefined;
  /** Every place: opens one place. */
  onOpenPlace?: ((placeId: string) => void) | undefined;
  /** The tech's own van while his count is with the office: on hand says "hidden". */
  isHidden?: boolean | undefined;
  /** No rows: what it says ("Nothing on Riley’s truck yet."). */
  empty?: ReactNode;
  /** Narrow (a phone), one place: no Last counted or At cost column (the item's sheet has them); "to count"
   *  moves after the item's code, so on hand stays narrow and the table fits the width. */
  narrow?: boolean | undefined;
}

const TO_COUNT: LineWord = { word: 'to count', tone: 'wait' };
const NO_MIN: LineWord = { word: 'no min', tone: 'wait' };

function onHand(l: StockLevel, unit: string, every: boolean): LineCell {
  const q = qtyWords(l.onHand, unit);
  if (Number(l.onHand) < 0)
    return { tone: 'problem', marks: [{ word: 'below zero' }], value: <strong>{q}</strong> };
  if (isBelowMin(l))
    return {
      tone: 'problem',
      marks: [{ word: every ? `below ${qtyWords(l.min, unit)}` : 'below min' }],
      value: <strong>{q}</strong>,
    };
  if (!every) return q;
  const marks = [...(hasNoMin(l) ? [NO_MIN] : []), ...(l.countNeeded ? [TO_COUNT] : [])];
  return marks.length ? { value: q, marks } : q;
}

/** An item's name (opening its sheet) and code; narrow, "to count" (it waits) after the code. */
function itemCell(r: StockItemRow, onOpenItem?: (id: string) => void, toCount?: boolean): LineCell {
  return {
    value: onOpenItem ? <Link onPress={() => onOpenItem(r.itemId)}>{r.itemName}</Link> : r.itemName,
    ...(onOpenItem ? {} : { look: 'strong' as const }),
    aside: r.code,
    ...(toCount ? { after: TO_COUNT } : {}),
  };
}

/** Rows by category, in the order they come; each head says how many and how many are below min. */
function byCategory(
  rows: StockItemRow[],
  place: string | null,
  draw: (r: StockItemRow) => Record<string, LineCell>,
): LineGroup[] {
  const groups = new Map<string, { name: string; rows: StockItemRow[] }>();
  for (const r of rows) {
    const g = groups.get(r.category.id) ?? { name: r.category.name, rows: [] };
    g.rows.push(r);
    groups.set(r.category.id, g);
  }
  return [...groups.entries()].map(([id, g]): LineGroup => {
    const levels = place ? g.rows.map((r) => r.levels[place]).filter((l): l is StockLevel => !!l) : [];
    const below = levels.filter(isBelowMin).length;
    const noMin = levels.filter(hasNoMin).length;
    return {
      id,
      label: g.name,
      ...(place
        ? {
            says: [
              g.rows.length,
              ...(below ? [{ problem: `${below} below min` }] : []),
              ...(noMin ? [`${noMin} no min`] : []),
            ],
          }
        : {}),
      rows: g.rows.map((r) => ({ id: r.itemId, cells: draw(r) })),
    };
  });
}

/** A place's stock, or every place's side by side, as LineTable draws it. */
export function stockLines({
  places,
  rows,
  onOpenItem,
  onOpenPlace,
  isHidden,
  empty,
  narrow,
}: StockLinesOptions): LineTableProps {
  const one = places.length === 1 ? places[0]! : null;
  const none =
    empty ?? (one ? `Nothing ${one.kind === 'truck' ? 'on' : 'in'} ${one.name} yet.` : 'No stock yet.');
  if (one) {
    const cost = !narrow && rows.some((r) => r.levels[one.id] && 'costCents' in r.levels[one.id]!);
    // Last counted only where the answer says it (absent: not known, so no column of guesses)
    const counted = !narrow && rows.some((r) => r.levels[one.id]?.lastCounted !== undefined);
    const columns: LineColumn[] = [
      { id: 'item', label: 'Item' },
      { id: 'on', label: 'On hand', align: 'end' },
      { id: 'min', label: 'Min', align: 'end' },
      { id: 'max', label: 'Max', align: 'end' },
      ...(counted ? [{ id: 'counted', label: 'Last counted', align: 'end' } as const] : []),
      ...(cost ? [{ id: 'cost', label: 'At cost', align: 'end' } as const] : []),
    ];
    const groups = byCategory(rows, isHidden ? null : one.id, (r) => {
      const l = r.levels[one.id]!;
      const noMin = hasNoMin(l);
      return {
        item: itemCell(r, onOpenItem, !counted && !!l.countNeeded),
        on: isHidden ? { tone: 'none', value: 'hidden' } : onHand(l, r.unit, false),
        min: noMin ? { value: 'no min', look: 'wait' } : qtyWords(l.min, r.unit),
        max: noMin ? { tone: 'none', value: '–' } : qtyWords(l.max, r.unit),
        ...(!counted
          ? {}
          : {
              counted: {
                value: l.lastCounted ?? 'never',
                ...(l.lastCounted ? {} : { tone: 'none' as const }),
                ...(l.countNeeded ? { marks: [TO_COUNT] } : {}),
              },
            }),
        ...(cost ? { cost: l.costCents == null ? { tone: 'none', value: '–' } : money(l.costCents) } : {}),
      };
    });
    return { label: `Stock on ${one.name}`, columns, groups, empty: none };
  }
  const columns: LineColumn[] = [
    { id: 'item', label: 'Item' },
    ...places.map((p) => ({
      id: p.id,
      label: onOpenPlace ? <Link onPress={() => onOpenPlace(p.id)}>{p.name}</Link> : p.name,
      align: 'end' as const,
    })),
  ];
  const groups = byCategory(rows, null, (r) => ({
    item: itemCell(r, onOpenItem),
    ...Object.fromEntries(
      places.map((p) => {
        const l = r.levels[p.id];
        return [p.id, l ? onHand(l, r.unit, true) : { tone: 'none', value: '–' }];
      }),
    ),
  }));
  return { label: 'Stock in every place', columns, groups, empty: none };
}
